import { cloneDeep } from 'es-toolkit';

import typeCheckUtils from '../utils/typeCheckUtils';

import { Adproduct } from './Adproduct';
import AIDescription from './AIDescription';
import ApplicationsSummary from './ApplicationsSummary';
import Freemium from './Freemium';
import RentGuarantee from './RentGuarantee';
import SingleFamilyContract from './SingleFamilyContract';
import AddressRestrictions from './AddressRestrictions';
import UpdateActiveStatus from './UpdateActiveStatus';
import LocaleSpecificNotice from './LocaleSpecificNotice';
import Photo from './Photo';
import PetsAllowed from './PetsAllowed';
import RentersInsurance from './RentersInsurance';
import Syndication from './Syndication';
import ContactEmailDeliverability from './ContactEmailDeliverability';
import TopDupesDetails from './TopDupesDetails';
import MultiToSingleFamilyConversion from './MultiToSingleFamilyConversion';
import SelfServeOnboardingStatus from './SelfServeOnboardingStatus';
import FloorPlan from './FloorPlan';
// listingCriteria related
import ListingCriteria from './ListingCriteria';
import MultiFamilyLogo from './MultiFamilyLogo';
import RoomForRent from './RoomForRent';
import SpecialOffer from './SpecialOffer';
import ApartmentAmenity from './ApartmentAmenity';
import WebAnalytics from './WebAnalytics';
import BuildingAttributes from './BuildingAttributes';
import DeliveryMethod from './DeliveryMethod';
import CurrentPropertyRestrictions from './CurrentPropertyRestrictions';
import { ListingAvailability } from './ListingAvailability';
import PartialListing from './PartialListing';
import Amenities from './Amenities';
import Performance from './Performance';
import ContactHours from './ContactHours';
import TotalMonthlyPriceWithFeeNotice from './TotalMonthlyPriceWithFeeNotice';
import TotalMonthlyPriceWithFee from './TotalMonthlyPriceWithFee';

const CurrentProperty = ({
  active = false, // from details endpoint
  adproduct = {},
  additionalEmail = '',
  applicationsSummary = {},
  addressRestrictions = {},
  aiDescription = {},
  agentLicenseType = null,
  propertyAmenities = [],
  amenities = {},
  balconyOrDeck = false,
  canBeArchived = true,
  canBeRelisted = false,
  city = '',
  contactEmail = '',
  contactEmailDeliverability = {},
  contactName = '',
  contactPhone = '',
  contactPhoneDateVerified = '',
  customerId = '',
  dateCreated = '',
  dateLastActivated = '',
  dateLastActivatedTimestamp = '',
  dateUpdated = '',
  dateUpdatedTimestamp = '',
  daysExtended = 0,
  deliveryMethod = {},
  description = '',
  dateEmailVerified = '',
  displayTotalMonthlyPriceWithFee = false,
  expirationDate = '',
  expirationDateTimestamp = '',
  externalTours = [],
  feedId = '',
  feedListingId = '',
  feedName = '',
  floorplans = [],
  forRentBy = '',
  forSaleExists = false,
  forSaleUrlOnZillow = '',
  freemium = {},
  hasHardwoodFloorCovering = false,
  hasActiveMultifamilyContract = false,
  hasAnalyticsRentComps = false,
  hasCentralAirConditioning = false,
  hasWallAirConditioning = false,
  hasWindowAirConditioning = false,
  hasForcedAirHeatingSystem = false,
  hasBaseboardHeatingSystem = false,
  hasHeatPumpHeatingSystem = false,
  hasWallHeatingSystem = false,
  hasAttachedGarageParking = false,
  hasBicycleStorage = false,
  hasDetachedGarageParking = false,
  hasDishWasherAppliance = false,
  hasSwimmingPool = false,
  hideAddress = false,
  hidePhone = false,
  housingConnectorStatus = '',
  isActivationBlocked = false,
  isAddressEditable = true,
  isAddressValid = true,
  isApplicationsAccepted = null,
  isApplicationsInviteOnly = false,
  isApplicationsInviteOnlyEnabled = false,
  isArchived = false,
  isEditable = true,
  isFeed = false,
  isFurnished = false,
  canSubmitMapPinRequest = false,
  isListingExclusive = false,
  isMultifamily = false,
  isPackageProduct = false,
  isPaidMultifamily = false,
  isViewable = false,
  isWaitlisted = false,
  laundry = '',
  leaseDuration = '',
  leaseTerms = '',
  listingAlias = '',
  listingAvailability = {},
  listingCriteria = {},
  listingTitle = '',
  listingTypeCode = -1,
  localeSpecificNotice = {},
  modularListingProgress = {},
  multiToSingleFamilyConversion = {}, // FP/units converted from MF
  name = '',
  numUnits = '',
  offStreetParking = false,
  partialListing = {},
  performance = undefined,
  petsAllowed = {},
  photos = [],
  preferredContactEmail = '',
  productType = '',
  propertyType = '',
  propertyTypeCode = -1,
  rentalProtection = {},
  rentersInsurance = {},
  rentZestimate = 0,
  restrictions = {},
  roomForRent = {},
  selectedProductType = 5,
  selfServeOnboardingStatus = {},
  singleFamilyContract = {},
  state = '',
  street = '',
  syndication = {},
  topDupesDetails = {},
  totalMonthlyPriceWithFee = {},
  totalMonthlyPriceWithFeeNotice = {},
  trackingPhoneNumber = '',
  unit = '',
  units = [],
  updateActiveStatus = {},
  views = 0,
  vrModels = [],
  wheelchairAccess = false,
  zip = '',
  // MF only
  website = '',
  logo = {},
  contactHours = {},
  specialOffers = [],
  buildingAttributes = {},
  apartmentAmenities = [],
  isContactInfoEditable = false,
  videoUrl = '',
  webAnalytics = {},
} = {}) => ({
  adproduct: Adproduct(adproduct),
  active: Boolean(active),
  additionalEmail: String(additionalEmail),
  aiDescription: AIDescription(aiDescription),
  applicationsSummary: ApplicationsSummary(applicationsSummary),
  addressRestrictions: AddressRestrictions(addressRestrictions),
  agentLicenseType: String(agentLicenseType),
  propertyAmenities: cloneDeep(propertyAmenities),
  amenities: Amenities(amenities),
  balconyOrDeck: Boolean(balconyOrDeck),
  canBeArchived: Boolean(canBeArchived),
  canBeRelisted: Boolean(canBeRelisted),
  city: String(city),
  contactEmail: String(contactEmail),
  contactEmailDeliverability: ContactEmailDeliverability(contactEmailDeliverability),
  contactName: String(contactName),
  contactPhone: String(contactPhone),
  contactPhoneDateVerified: String(contactPhoneDateVerified),
  customerId: String(customerId),
  dateCreated: String(dateCreated),
  dateLastActivated: String(dateLastActivated),
  dateLastActivatedTimestamp: String(dateLastActivatedTimestamp),
  dateUpdated: String(dateUpdated),
  dateUpdatedTimestamp: String(dateUpdatedTimestamp),
  daysExtended: Number(daysExtended),
  deliveryMethod: DeliveryMethod(deliveryMethod),
  description: String(description),
  dateEmailVerified: String(dateEmailVerified),
  displayTotalMonthlyPriceWithFee: Boolean(displayTotalMonthlyPriceWithFee),
  expirationDate: String(expirationDate),
  expirationDateTimestamp: Number(expirationDateTimestamp),
  externalTours: cloneDeep(externalTours),
  feedId: String(feedId),
  feedListingId: String(feedListingId),
  feedName: String(feedName),
  floorplans: floorplans.map(FloorPlan),
  forRentBy: String(forRentBy),
  forSaleExists: Boolean(forSaleExists),
  forSaleUrlOnZillow: String(forSaleUrlOnZillow),
  freemium: Freemium(freemium),
  hasAttachedGarageParking: Boolean(hasAttachedGarageParking),
  hasHardwoodFloorCovering: Boolean(hasHardwoodFloorCovering),
  hasActiveMultifamilyContract: Boolean(hasActiveMultifamilyContract),
  hasAnalyticsRentComps: Boolean(hasAnalyticsRentComps),
  hasCentralAirConditioning: Boolean(hasCentralAirConditioning),
  hasWallAirConditioning: Boolean(hasWallAirConditioning),
  hasWindowAirConditioning: Boolean(hasWindowAirConditioning),
  hasForcedAirHeatingSystem: Boolean(hasForcedAirHeatingSystem),
  hasBaseboardHeatingSystem: Boolean(hasBaseboardHeatingSystem),
  hasHeatPumpHeatingSystem: Boolean(hasHeatPumpHeatingSystem),
  hasWallHeatingSystem: Boolean(hasWallHeatingSystem),
  hasBicycleStorage: Boolean(hasBicycleStorage),
  hasDetachedGarageParking: Boolean(hasDetachedGarageParking),
  hasDishWasherAppliance: Boolean(hasDishWasherAppliance),
  hasSwimmingPool: Boolean(hasSwimmingPool),
  hideAddress: Boolean(hideAddress),
  hidePhone: Boolean(hidePhone),
  housingConnectorStatus: String(housingConnectorStatus),
  isActivationBlocked: Boolean(isActivationBlocked),
  isAddressEditable: Boolean(isAddressEditable),
  isAddressValid: Boolean(isAddressValid),
  isApplicationsAccepted: typeCheckUtils.Boolean(isApplicationsAccepted),
  isApplicationsInviteOnly: Boolean(isApplicationsInviteOnly),
  isApplicationsInviteOnlyEnabled: Boolean(isApplicationsInviteOnlyEnabled),
  isArchived: Boolean(isArchived),
  isEditable: Boolean(isEditable),
  isFeed: Boolean(isFeed),
  isFurnished: Boolean(isFurnished),
  canSubmitMapPinRequest: Boolean(canSubmitMapPinRequest),
  isListingExclusive: Boolean(isListingExclusive),
  isMultifamily: Boolean(isMultifamily),
  isPackageProduct: Boolean(isPackageProduct),
  isPaidMultifamily: Boolean(isPaidMultifamily),
  isViewable: Boolean(isViewable),
  isWaitlisted: Boolean(isWaitlisted),
  laundry: String(laundry),
  leaseDuration: String(leaseDuration),
  leaseTerms: String(leaseTerms),
  listingAlias: String(listingAlias),
  listingAvailability: ListingAvailability(listingAvailability),
  listingCriteria: ListingCriteria(listingCriteria),
  listingTitle: String(listingTitle),
  listingTypeCode: Number(listingTypeCode),
  localeSpecificNotice: LocaleSpecificNotice(localeSpecificNotice),
  modularListingProgress: Object(modularListingProgress),
  // FP/units converted from MF
  multiToSingleFamilyConversion: MultiToSingleFamilyConversion(multiToSingleFamilyConversion),
  name: String(name),
  numUnits: String(numUnits),
  offStreetParking: Boolean(offStreetParking),
  partialListing: PartialListing(partialListing),
  performance: performance ? Performance(performance) : performance,
  petsAllowed: PetsAllowed(petsAllowed),
  photos: photos.map(Photo),
  preferredContactEmail: String(preferredContactEmail),
  price: Number(floorplans[0]?.['price']),
  productType: String(productType),
  propertyType: String(propertyType),
  propertyTypeCode: Number(propertyTypeCode),
  rentalProtection: RentGuarantee(rentalProtection),
  rentersInsurance: RentersInsurance(rentersInsurance),
  rentZestimate: Number(rentZestimate),
  restrictions: CurrentPropertyRestrictions(restrictions),
  roomForRent: RoomForRent(roomForRent),
  securityDeposit: Number(floorplans[0]?.['securityDeposit']),
  selectedProductType: Number(selectedProductType),
  selfServeOnboardingStatus: SelfServeOnboardingStatus(selfServeOnboardingStatus),
  singleFamilyContract: SingleFamilyContract(singleFamilyContract),
  state: String(state),
  street: String(street),
  syndication: Syndication(syndication),
  topDupesDetails: TopDupesDetails(topDupesDetails),
  totalMonthlyPriceWithFee: TotalMonthlyPriceWithFee(totalMonthlyPriceWithFee),
  totalMonthlyPriceWithFeeNotice: TotalMonthlyPriceWithFeeNotice(totalMonthlyPriceWithFeeNotice),
  trackingPhoneNumber: String(trackingPhoneNumber),
  unit: String(unit),
  units: cloneDeep(units),
  updateActiveStatus: UpdateActiveStatus(updateActiveStatus),
  views: Number(views),
  vrModels: cloneDeep(vrModels),
  wheelchairAccess: Boolean(wheelchairAccess),
  zip: String(zip),
  // MF only
  website: String(website),
  logo: MultiFamilyLogo(logo),
  contactHours: ContactHours(contactHours),
  specialOffers: specialOffers.map(SpecialOffer),
  buildingAttributes: BuildingAttributes(buildingAttributes),
  apartmentAmenities: apartmentAmenities.map(ApartmentAmenity),
  isContactInfoEditable: Boolean(isContactInfoEditable),
  videoUrl: String(videoUrl),
  webAnalytics: WebAnalytics(webAnalytics),
});

export default CurrentProperty;

export type CurrentPropertyProps = ReturnType<typeof CurrentProperty>;
